import { useEffect, useState, useCallback } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { can } from "@/lib/roles";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { fmtDate } from "@/lib/format";
import {
  Plus, Loader2, Trash2, Download, Mail, MessageSquare, CheckCircle2, RotateCcw, Send, FileText, Clock, Copy, ExternalLink,
} from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export const ApDocuments = ({ propertyId, user }) => {
  const canUpload = can(user, ["admin", "regional", "finance", "gm"]);
  const canForward = can(user, ["admin", "regional", "finance", "gm"]);
  const canComplete = can(user, ["admin", "regional"]);
  const canComment = can(user, ["admin", "regional", "finance", "gm"]);

  const [docs, setDocs] = useState(null);
  const [emailCfg, setEmailCfg] = useState({ email_configured: false, mail_from: "" });

  // upload
  const [upOpen, setUpOpen] = useState(false);
  const [upForm, setUpForm] = useState({ title: "", recipient_email: "", note: "" });
  const [upFile, setUpFile] = useState(null);
  const [upBusy, setUpBusy] = useState(false);

  // forward
  const [fwOpen, setFwOpen] = useState(false);
  const [fwDoc, setFwDoc] = useState(null);
  const [fwForm, setFwForm] = useState({ to: "", subject: "", message: "" });
  const [fwBusy, setFwBusy] = useState(false);
  const [fwLink, setFwLink] = useState("");

  // comments
  const [openComments, setOpenComments] = useState({});
  const [commentText, setCommentText] = useState({});

  const load = useCallback(() => {
    if (!propertyId) return;
    api.get("/ap-documents", { params: { property_id: propertyId } }).then((r) => setDocs(r.data)).catch(() => setDocs([]));
  }, [propertyId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/ap-documents/config").then((r) => setEmailCfg(r.data)).catch(() => {}); }, []);

  const submitUpload = async () => {
    if (!upFile) return toast.error("Choose a file to upload");
    setUpBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", upFile);
      await api.post("/ap-documents", fd, {
        params: { property_id: propertyId, title: upForm.title, recipient_email: upForm.recipient_email, note: upForm.note },
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Document uploaded");
      setUpOpen(false); setUpForm({ title: "", recipient_email: "", note: "" }); setUpFile(null);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setUpBusy(false); }
  };

  const openForward = (d) => {
    setFwDoc(d);
    setFwLink("");
    setFwForm({ to: d.recipient_email || "", subject: `Document: ${d.title}`, message: d.note ? `${d.note}\n\n` : "" });
    setFwOpen(true);
  };

  const submitForward = async () => {
    setFwBusy(true);
    try {
      await api.post(`/ap-documents/${fwDoc.id}/forward`, fwForm);
      toast.success("Email sent");
      setFwOpen(false);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setFwBusy(false); }
  };

  // No-API path: get a public download link + open the user's own email client
  const openInEmailApp = async () => {
    if (!fwForm.to.trim()) return toast.error("Enter a recipient email");
    setFwBusy(true);
    try {
      const { data } = await api.post(`/ap-documents/${fwDoc.id}/mailto`, { to: fwForm.to, subject: fwForm.subject });
      const link = `${BACKEND}/api/ap-documents/share/${data.token}`;
      setFwLink(link);
      const body = `${fwForm.message || ""}\n\nDownload the document here:\n${link}\n`;
      const href = `mailto:${encodeURIComponent(fwForm.to)}?subject=${encodeURIComponent(fwForm.subject || "Document")}&body=${encodeURIComponent(body)}`;
      window.location.href = href;
      toast.success("Opening your email app… the download link is included.");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setFwBusy(false); }
  };

  const download = async (d) => {
    try {
      const res = await api.get(`/ap-documents/${d.id}/file`, { params: { download: 1 }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = d.file?.filename || d.title || "document";
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) { toast.error("Could not download file"); }
  };

  const addComment = async (d) => {
    const text = (commentText[d.id] || "").trim();
    if (!text) return;
    try {
      await api.post(`/ap-documents/${d.id}/comments`, { text });
      setCommentText((m) => ({ ...m, [d.id]: "" }));
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const setComplete = async (d, complete) => {
    try {
      await api.post(`/ap-documents/${d.id}/complete`, { complete });
      toast.success(complete ? "Marked complete" : "Reopened");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const del = async (d) => {
    if (!window.confirm(`Delete "${d.title}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/ap-documents/${d.id}`);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div className="mt-8" data-testid="ap-documents-section">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-heading text-lg font-bold">Documents</h2>
          <p className="text-sm text-muted-foreground">Documents received at the property that need to be forwarded to a third party (e.g. accountant, insurer).</p>
        </div>
        {canUpload && (
          <Button onClick={() => setUpOpen(true)} disabled={!propertyId} data-testid="ap-doc-upload-btn">
            <Plus className="mr-2 h-4 w-4" /> Upload Document
          </Button>
        )}
      </div>

      {!emailCfg.email_configured && (
        <div className="mb-4 rounded-none border border-blue-400/50 bg-blue-50 px-4 py-2 text-xs text-blue-800 dark:bg-blue-950/20 dark:text-blue-300" data-testid="ap-doc-email-notice">
          <span className="font-medium">Forwarding works with no setup:</span> "Forward" opens your own email app with a secure download link already in the message — just review and send. (Automatic sending with the file attached turns on later if an admin adds the office email credentials.)
        </div>
      )}

      <Card className="rounded-none border">
        {docs === null ? (
          <div className="flex h-32 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
        ) : docs.length === 0 ? (
          <div className="p-10 text-center">
            <FileText className="mx-auto h-8 w-8 text-muted-foreground" />
            <p className="mt-3 text-sm text-muted-foreground">No documents yet.</p>
          </div>
        ) : (
          <div className="divide-y">
            {docs.map((d) => {
              const done = d.status === "complete";
              return (
                <div key={d.id} className={`p-4 ${done ? "bg-muted/40" : ""}`} data-testid={`ap-doc-row-${d.id}`}>
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <button onClick={() => download(d)} className="truncate text-left font-medium hover:underline" data-testid={`ap-doc-title-${d.id}`}>{d.title}</button>
                        {done ? (
                          <Badge className="bg-emerald-600" data-testid={`ap-doc-status-${d.id}`}>Complete</Badge>
                        ) : (
                          <Badge variant="secondary" data-testid={`ap-doc-status-${d.id}`}>Open</Badge>
                        )}
                        {(d.email_log?.length || 0) > 0 && (
                          <Badge variant="outline" className="gap-1"><Mail className="h-3 w-3" /> Sent {d.email_log.length}×</Badge>
                        )}
                      </div>
                      <div className="mt-1 text-xs text-muted-foreground">
                        Uploaded by {d.uploaded_by} · {fmtDate((d.created_at || "").slice(0, 10))}
                        {d.recipient_email && <> · Send to <span className="font-medium text-foreground">{d.recipient_email}</span></>}
                      </div>
                      {d.note && <p className="mt-1 text-sm">{d.note}</p>}
                      {done && d.completed_by && (
                        <p className="mt-1 flex items-center gap-1 text-xs text-emerald-700 dark:text-emerald-400"><CheckCircle2 className="h-3 w-3" /> Completed by {d.completed_by} · {fmtDate((d.completed_at || "").slice(0, 10))}</p>
                      )}
                    </div>
                    <div className="flex shrink-0 flex-wrap items-center gap-1">
                      <Button size="sm" variant="ghost" onClick={() => download(d)} data-testid={`ap-doc-download-${d.id}`}><Download className="h-4 w-4" /></Button>
                      {canForward && (
                        <Button size="sm" variant="outline" onClick={() => openForward(d)} data-testid={`ap-doc-forward-${d.id}`}><Mail className="mr-1 h-4 w-4" /> Forward</Button>
                      )}
                      {canComment && (
                        <Button size="sm" variant="ghost" onClick={() => setOpenComments((m) => ({ ...m, [d.id]: !m[d.id] }))} data-testid={`ap-doc-comment-toggle-${d.id}`}>
                          <MessageSquare className="mr-1 h-4 w-4" /> {d.comments?.length || 0}
                        </Button>
                      )}
                      {canComplete && (done ? (
                        <Button size="sm" variant="ghost" onClick={() => setComplete(d, false)} data-testid={`ap-doc-reopen-${d.id}`}><RotateCcw className="mr-1 h-4 w-4" /> Reopen</Button>
                      ) : (
                        <Button size="sm" variant="ghost" className="text-emerald-600" onClick={() => setComplete(d, true)} data-testid={`ap-doc-complete-${d.id}`}><CheckCircle2 className="mr-1 h-4 w-4" /> Complete</Button>
                      ))}
                      <Button size="sm" variant="ghost" onClick={() => del(d)} data-testid={`ap-doc-delete-${d.id}`}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                    </div>
                  </div>

                  {openComments[d.id] && (
                    <div className="mt-3 space-y-2 border-l-2 border-muted pl-3" data-testid={`ap-doc-comments-${d.id}`}>
                      {(d.email_log || []).map((e, i) => (
                        <p key={`e${i}`} className="flex items-center gap-1 text-xs text-blue-700 dark:text-blue-400">
                          <Send className="h-3 w-3" /> Emailed to {Array.isArray(e.to) ? e.to.join(", ") : e.to} by {e.sent_by} · {fmtDate((e.sent_at || "").slice(0, 10))}
                        </p>
                      ))}
                      {(d.comments || []).map((c, i) => (
                        <div key={`c${i}`} className="text-sm">
                          <span className="font-medium">{c.by}</span>
                          <span className="ml-2 text-xs text-muted-foreground">{fmtDate((c.at || "").slice(0, 10))}</span>
                          <p className="text-muted-foreground">{c.text}</p>
                        </div>
                      ))}
                      {canComment && (
                        <div className="flex items-center gap-2 pt-1">
                          <Input
                            value={commentText[d.id] || ""}
                            onChange={(e) => setCommentText((m) => ({ ...m, [d.id]: e.target.value }))}
                            onKeyDown={(e) => e.key === "Enter" && addComment(d)}
                            placeholder="Add a comment (e.g. GM emailed to accountant)…"
                            className="h-8"
                            data-testid={`ap-doc-comment-input-${d.id}`}
                          />
                          <Button size="sm" onClick={() => addComment(d)} data-testid={`ap-doc-comment-add-${d.id}`}>Add</Button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </Card>

      {/* Upload dialog */}
      <Dialog open={upOpen} onOpenChange={setUpOpen}>
        <DialogContent data-testid="ap-doc-upload-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Upload Document</DialogTitle>
            <DialogDescription>A document to be forwarded to a third party. For a vendor bill, use "Upload Invoice" instead.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label>Title</Label>
              <Input value={upForm.title} onChange={(e) => setUpForm((f) => ({ ...f, title: e.target.value }))} placeholder="e.g. W-9 for Accountant" data-testid="ap-doc-title-input" />
            </div>
            <div className="space-y-1.5">
              <Label>Send to (email) — optional</Label>
              <Input type="email" value={upForm.recipient_email} onChange={(e) => setUpForm((f) => ({ ...f, recipient_email: e.target.value }))} placeholder="accountant@example.com" data-testid="ap-doc-recipient-input" />
              <p className="text-xs text-muted-foreground">Suggest where this should go so management knows who to forward it to.</p>
            </div>
            <div className="space-y-1.5">
              <Label>Note — optional</Label>
              <Textarea value={upForm.note} onChange={(e) => setUpForm((f) => ({ ...f, note: e.target.value }))} rows={2} placeholder="Any context for management" data-testid="ap-doc-note-input" />
            </div>
            <div className="space-y-1.5">
              <Label>File *</Label>
              <label className="flex cursor-pointer items-center gap-2 border border-dashed px-3 py-2 text-sm text-muted-foreground">
                <FileText className="h-4 w-4" />
                {upFile ? upFile.name : "Choose file (PDF, image, Excel, Word)"}
                <input type="file" accept=".pdf,.jpg,.jpeg,.png,.xlsx,.xls,.csv,.doc,.docx" className="hidden" onChange={(e) => setUpFile(e.target.files?.[0] || null)} data-testid="ap-doc-file-input" />
              </label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setUpOpen(false)}>Cancel</Button>
            <Button onClick={submitUpload} disabled={upBusy} data-testid="ap-doc-upload-submit">{upBusy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null} Upload</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Forward dialog */}
      <Dialog open={fwOpen} onOpenChange={setFwOpen}>
        <DialogContent data-testid="ap-doc-forward-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Forward via Email</DialogTitle>
            <DialogDescription>{emailCfg.email_configured ? `${emailCfg.mail_from ? "Sends from " + emailCfg.mail_from + " — " : ""}${fwDoc?.file?.filename} is attached.` : "Sends from your own email app with a secure download link to the document."}</DialogDescription>
          </DialogHeader>
          {!emailCfg.email_configured ? (
            <div className="space-y-4 py-2" data-testid="ap-doc-forward-mailto">
              <div className="rounded-none border border-blue-400/40 bg-blue-50 p-3 text-xs text-blue-800 dark:bg-blue-950/20 dark:text-blue-300">
                No email setup needed — this opens <span className="font-medium">your own email app</span> with a secure download link to the document already in the message. Just review and hit send.
              </div>
              <div className="space-y-1.5">
                <Label>To *</Label>
                <Input type="email" value={fwForm.to} onChange={(e) => setFwForm((f) => ({ ...f, to: e.target.value }))} placeholder="name@example.com" data-testid="ap-doc-forward-to" />
              </div>
              <div className="space-y-1.5">
                <Label>Subject</Label>
                <Input value={fwForm.subject} onChange={(e) => setFwForm((f) => ({ ...f, subject: e.target.value }))} data-testid="ap-doc-forward-subject" />
              </div>
              <div className="space-y-1.5">
                <Label>Message</Label>
                <Textarea value={fwForm.message} onChange={(e) => setFwForm((f) => ({ ...f, message: e.target.value }))} rows={3} data-testid="ap-doc-forward-message" />
              </div>
              {fwLink && (
                <div className="space-y-1.5">
                  <Label>Secure download link</Label>
                  <div className="flex items-center gap-2">
                    <Input readOnly value={fwLink} className="text-xs" data-testid="ap-doc-forward-link" />
                    <Button size="icon" variant="outline" onClick={() => { navigator.clipboard?.writeText(fwLink); toast.success("Link copied"); }} data-testid="ap-doc-forward-copy"><Copy className="h-4 w-4" /></Button>
                  </div>
                  <p className="text-xs text-muted-foreground">If your email app didn't open, copy this link into your email manually.</p>
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-4 py-2">
              <div className="space-y-1.5">
                <Label>To *</Label>
                <Input type="email" value={fwForm.to} onChange={(e) => setFwForm((f) => ({ ...f, to: e.target.value }))} placeholder="name@example.com (comma-separate for multiple)" data-testid="ap-doc-forward-to" />
              </div>
              <div className="space-y-1.5">
                <Label>Subject</Label>
                <Input value={fwForm.subject} onChange={(e) => setFwForm((f) => ({ ...f, subject: e.target.value }))} data-testid="ap-doc-forward-subject" />
              </div>
              <div className="space-y-1.5">
                <Label>Message</Label>
                <Textarea value={fwForm.message} onChange={(e) => setFwForm((f) => ({ ...f, message: e.target.value }))} rows={4} data-testid="ap-doc-forward-message" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setFwOpen(false)}>Cancel</Button>
            {emailCfg.email_configured ? (
              <Button onClick={submitForward} disabled={fwBusy} data-testid="ap-doc-forward-submit">{fwBusy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Mail className="mr-2 h-4 w-4" />} Send</Button>
            ) : (
              <Button onClick={openInEmailApp} disabled={fwBusy} data-testid="ap-doc-forward-mailto-btn">{fwBusy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <ExternalLink className="mr-2 h-4 w-4" />} Open in Email App</Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
